import { Fragment } from 'react';
import {
  Divider,
  H3,
  Inline,
  InlineItem,
  Secondary,
  Stack,
  Text,
} from '@kinesis/bungle';
import { noop, sumBy } from 'lodash/fp';
import { formatDayString } from '@/date';
import { ExportButton } from './export-button';
import type { OrganisationExport } from '@/api/exports';

const units = ['B', 'KB', 'MB', 'GB', 'TB'];

const formatSize = (bytes: number) => {
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${size} B` : `${size.toFixed(1)} ${units[unit]}`;
};

const formatFileCount = (count: number) =>
  count === 1 ? '1 file' : `${count} files`;

type Props = {
  organisationExport: OrganisationExport;
  // This export's parts whose links are being generated.
  downloadingParts?: number[];
  unavailable?: boolean;
  onDownload?: (exportId: string, n: number) => void;
};

const OrganisationExportItem = ({
  organisationExport,
  downloadingParts = [],
  unavailable = false,
  onDownload = noop,
}: Props) => {
  const { id, name, createdAt, expiresAt, parts } = organisationExport;
  const fileCount = sumBy('fileCount', parts);
  const size = sumBy('size', parts);

  return (
    <Stack space='small'>
      <Stack space='xsmall'>
        <H3>{name}</H3>
        <Text>
          <Secondary>
            Created {formatDayString(createdAt)} · Available until{' '}
            {formatDayString(expiresAt)} · {formatFileCount(fileCount)},{' '}
            {formatSize(size)}
          </Secondary>
        </Text>
      </Stack>
      {unavailable ? (
        <Text>
          <Secondary>This export is no longer available</Secondary>
        </Text>
      ) : (
        parts.map((part) => (
          <Fragment key={part.n}>
            <Divider />
            <Inline alignY='center'>
              <InlineItem sizing='fill-container'>
                <Stack space='xsmall'>
                  <Text>
                    Part {part.n} of {parts.length}
                  </Text>
                  <Text>
                    <Secondary>
                      {formatSize(part.size)} ·{' '}
                      {formatFileCount(part.fileCount)}
                    </Secondary>
                  </Text>
                </Stack>
              </InlineItem>
              <InlineItem>
                <ExportButton
                  downloading={downloadingParts.includes(part.n)}
                  onClick={() => onDownload(id, part.n)}
                >
                  Download part {part.n}
                </ExportButton>
              </InlineItem>
            </Inline>
          </Fragment>
        ))
      )}
    </Stack>
  );
};

export { OrganisationExportItem };
